import {
  Inject,
  PLATFORM_ID,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewChild,
  ElementRef,
  WritableSignal,
  signal,
  effect,
  computed,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { BurgerButtonComponent } from './burger-button/burger-button.component';
import {
  AppAutocompleteSearch,
  ACSOption,
  SearchButtonConfig,
} from '../../uikit/autocomplete/autocomplete.component';
import { SearchStaxService } from '../../services/searchstax.service';
import { SearchStaxSuggestion } from '../../models/searchstax.model';
import { ClickOutsideDirective } from '@uikit/click-outside/click-outside.directive';
import { PromotionService } from '../../../app/services/promotion.service';
import { NgxsModule, Select } from '@ngxs/store';
import { UserState, UserStateModel } from '../../../app/store/user/state/user.state';
import {
  Observable,
  Subject,
  map,
  takeUntil,
  filter,
  tap,
  timer,
  switchMap,
  firstValueFrom,
} from 'rxjs';
import { NavigationStart, NavigationEnd } from '@angular/router';
import { BrowseSpecialtiesComponent } from '../browse-specialties/browse-specialties.component';
import { AppButton } from '../../uikit/button/button.component';
import { SpecialtyService } from '../../../app/services';
import { CookieService } from 'ngx-cookie-service';
import { GoogleDataLayerService, AuthService } from '../../../app/services';
import { CommonModule } from '@angular/common';
import { UserModel } from '../../../app/models';
import { Accordion, AccordionPanel, AccordionHeader, AccordionContent } from 'primeng/accordion';

@Component({
  selector: 'ama-ribbon',
  templateUrl: 'ribbon.component.html',
  styleUrls: ['ribbon.component.scss'],
  imports: [
    CommonModule,
    NgxsModule,
    ClickOutsideDirective,
    NgClass,
    BurgerButtonComponent,
    AppAutocompleteSearch,
    AppButton,
    BrowseSpecialtiesComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [GoogleDataLayerService, CookieService],
})
export class RibbonComponent implements OnInit {
  @Input() public user: UserModel = new UserModel();
  @Input() public freidaNavigation = [];
  @Input() public amaExploreMenuItems = [];
  @Input() public userMenuGroup = [];
  @Input() public joinButtons = [];
  @Input() public searchSubmit = false;
  @Output() public isOpen = false;
  @Output() public userLogOut = new EventEmitter<any>();
  @Output() public searchOpen = false;
  @Output() public color = '#1976d2';
  @Output() public menuOpen = false;
  @Output() public submitSearchEvent = new EventEmitter();
  public readonly signInClicked = output<void>();

  public isUserOpen = false;
  public hideCreateAccountLink = false;
  public isModalOpen = false;
  public userMenu = [];
  public disableUpdates$ = signal(false);

  @Select(UserState.isMember)
  public isMember$: Observable<boolean>;

  @Select(UserState)
  public user$: Observable<UserStateModel>;
  public currentUser: WritableSignal<UserModel> = signal(new UserModel());
  public readonly logoUrl = computed(() =>
    this.currentUser().isMember || this.currentUser().isAuthenticated
      ? '/dashboard-landing'
      : this.lastUnauthHomeUrl(),
  );

  readonly resetTypeaheadToken = signal(0);
  readonly options = signal<ACSOption<SearchStaxSuggestion>[]>([]);
  readonly suggestions = signal<ACSOption<SearchStaxSuggestion>[] | null>(null);
  private readonly query = signal('');
  private lastQuery = '';
  private debounceHandle: any;
  private reqId = 0;
  private readonly debounceMs = 300;
  private readonly lastUnauthHomeUrl = signal<string>('/');

  public readonly searchButton: SearchButtonConfig = {
    variant: 'text',
    size: 'sm',
    icon: 'magnifying-glass',
  };
  public readonly panelClass = 'app-acs-panel--ribbon';

  private destroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('focusCheck') focusCheck!: ElementRef;
  @ViewChild('focusEnd') focusEnd!: ElementRef;
  @ViewChild('burgerButton') burgerButton!: BurgerButtonComponent;

  public isInputFocused: boolean = false;
  public isUserMenuFocused: boolean = false;

  constructor(
    private router: Router,
    public specialtyService: SpecialtyService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dataLayerService: GoogleDataLayerService,
    protected promotionService: PromotionService,
    private readonly searchstax: SearchStaxService,
    @Inject(PLATFORM_ID) private platform,
  ) {
    this.isOpen = false;

    effect(() => {
      const q = this.query().trim();
      if (q === this.lastQuery) return;
      this.lastQuery = q;
      clearTimeout(this.debounceHandle);
      this.debounceHandle = setTimeout(() => this.runSearch(q), this.debounceMs);
    });

    // Only subscribe to disableUpdates in browser (not during SSR)
    if (isPlatformBrowser(this.platform)) {
      effect(() => {
        this.disableUpdates$.set(this.promotionService.disableUpdates());
      });
    }

    this.user$
      .pipe(
        map((userState) => new UserModel(userState.user)),
        takeUntil(this.destroy$),
      )
      .subscribe((user) => this.currentUser.set(user));

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.searchOpen = false;
      });
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((e: NavigationEnd) => {
        const url = e.urlAfterRedirects;
        if (url === '/') {
          this.lastUnauthHomeUrl.set('/');
        } else if (url.startsWith('/fellowship')) {
          this.lastUnauthHomeUrl.set('/fellowship');
        }
        const isOnSearchList = url.startsWith('/search/');
        if (!isOnSearchList) {
          this.resetTypeaheadToken.update((v) => v + 1);
        }
      });
  }

  public ngOnInit() {
    // Only show user menu tooltip in browser (not during SSR)
    // The 8-second timer would block SSR from completing
    if (isPlatformBrowser(this.platform)) {
      const userMenu = this.cookieService.get('userMenuSeen');
      // A signed-in session cookie is set synchronously by the server before the
      // post-login redirect completes, well before the user store hydrates via the
      // async UpdateUser action. Checking it here (instead of isMember$/currentUser())
      // avoids a race where the tooltip opens for an instant using the stale
      // anonymous default state right after sign-in.
      const hasSession =
        this.cookieService.check('FreidaJwt') || !!this.cookieService.get('PD-H-SESSION-ID');
      if (!userMenu && !hasSession) {
        this.isMember$
          .pipe(
            takeUntil(this.destroy$),
            filter((member) => !member && userMenu === ''),
            tap(() => this.toggleUserMenu()),
            switchMap(() => timer(8000)),
            tap(() => {
              this.toggleUserMenu();
              this.cookieService.set('userMenuSeen', 'true');
            }),
          )
          .subscribe();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openSpecialtiesModal() {
    this.isOpen = false;
    this.isModalOpen = !this.isModalOpen;
  }

  public navigateTo(header_area: string, text: string, url: string, e: Event) {
    e.preventDefault();
    this.dataLayerService.trackRibbonAnalytics(header_area, text, url);
    this.closeMenu();
    this.router.navigateByUrl(url);
  }

  public navigateToExternalUrl(header_area: string, text: string, url: string, e: Event) {
    e.preventDefault();
    this.dataLayerService.trackRibbonAnalytics(header_area, text, url);
    this.closeMenu();
    window.open(url, '_blank');
  }

  public toggleMenu() {
    this.isOpen = this.menuOpen;
  }

  public toggleSearchMenu() {
    this.isOpen = false;
    this.searchOpen = !this.searchOpen;
  }

  public toggleUserMenu() {
    if (!this.isUserOpen) {
      this.authService.clearStaleSessionIfNeeded();
    }

    this.isUserOpen = !this.isUserOpen;
    if (this.isUserOpen) {
      const user = this.currentUser();
      if (user?.roles && !user.roles.includes('anonymous')) {
        this.hideCreateAccountLink = true;
      }
    }
  }

  public onSubmitSearch() {
    this.isOpen = false;
    this.isModalOpen = false;
    this.searchOpen = false;
  }

  public onTypeahead(q: string) {
    this.query.set(q);
  }

  public onCleared() {
    this.reqId++;
    this.query.set('');
    this.options.set([]);
    this.suggestions.set(null);
  }

  public onSelectedValue(val: SearchStaxSuggestion) {
    this.onSubmitSearch();
    switch (val.type) {
      case 'program':
        this.router.navigateByUrl('/program/' + val.programId!.trim());
        break;
      case 'specialty':
        this.router.navigate(['/search/list'], {
          queryParams: { spec: val.nid!.trim() },
          state: { gtmSearchTerm: val.title },
        });
        break;
    }
  }

  public onSearch(e: { search: string; selected?: SearchStaxSuggestion }) {
    const sel = e.selected;
    if (sel) {
      this.onSelectedValue(sel);
      return;
    }
    this.onSubmitSearch();
    const q = (e.search ?? '').trim();
    this.router.navigateByUrl('/search/list?query=' + encodeURIComponent(q));
  }

  private async runSearch(q: string) {
    if (q.length <= 2) {
      this.reqId++;
      this.options.set([]);
      this.suggestions.set(null);
      return;
    }
    const id = ++this.reqId;
    try {
      const res = await firstValueFrom(this.searchstax.getSuggestions(q, 'Residency', true));
      if (id !== this.reqId) return;
      this.options.set(res.specialties.map((s) => ({ label: s.title, value: s })));
      this.suggestions.set(
        res.programs.map((s) => ({
          label: s.title,
          value: s,
          extra: [s.specialtyTitle].filter(Boolean).join(' • '),
        })),
      );
    } catch {
      if (id !== this.reqId) return;
      this.options.set([]);
      this.suggestions.set(null);
    }
  }

  public searchSpecialty($event): void {
    let converted = [];
    if ($event.term === '') {
      if (this.specialtyService.getSelectedSpecs()) {
        converted = this.specialtyService.getSelectedSpecs().map((specialty) => {
          return specialty.nid;
        });
      }
      const route = this.router.url;
      if (route.includes('map')) {
        this.router.navigate(['/search/map'], {
          queryParamsHandling: 'merge',
          queryParams: { spec: converted.join(','), fromHeader: true, loc: null },
        });
      } else {
        this.router.navigate(['/search/list'], {
          queryParamsHandling: 'merge',
          queryParams: { spec: converted.join(','), fromHeader: true, loc: null },
        });
      }
    } else {
      this.router.navigate(['program', $event.term]);
    }
  }

  public goToDashboard() {
    this.dataLayerService.trackRibbonAnalytics(
      'sign in dropdown',
      'Go to Dashboard',
      '/dashboard-landing',
    );
    this.menuOpen = false;
    this.isOpen = false;
    this.router.navigate(['/dashboard-landing']);
  }

  public closeMenu() {
    this.isOpen = false;
    this.menuOpen = false;
  }

  public closeUserMenu() {
    this.isUserOpen = false;
  }

  public checkForLogout(item) {
    if (isPlatformBrowser(this.platform)) {
      if (item === 'Sign In') {
        this.dataLayerService.trackRibbonAnalytics('sign in dropdown', 'Sign In', '/login');
        this.signInClicked.emit();
      } else {
        this.dataLayerService.trackRibbonAnalytics('sign in dropdown', 'Log out', '/logout');
        this.authService.logout(window.location.href);
      }
    }
  }

  public checkFocus(menuShouldClose) {
    this.isInputFocused =
      isPlatformBrowser(this.platform) &&
      (document.activeElement === this.focusCheck.nativeElement ||
        document.activeElement === this.focusEnd.nativeElement);
    if (menuShouldClose) {
      this.isOpen = false;
      this.menuOpen = false;
    } else {
      this.isOpen = true;
      this.menuOpen = true;
    }
  }

  public onBackwardsKeyboardNav(event: KeyboardEvent) {
    this.isInputFocused =
      isPlatformBrowser(this.platform) &&
      (document.activeElement === this.burgerButton?.focusBegin?.nativeElement ||
        document.activeElement === this.focusEnd.nativeElement);
    if (event.shiftKey && event.code === 'Tab') {
      this.isOpen = false;
      this.menuOpen = false;
      this.isUserOpen = false;
    }
  }
}
